import { groupBy, summarize, tidy } from '@tidyjs/tidy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Flex } from '../../Components/Base';
import Loading from '../../Components/Loading';
import LoLExamRoomAppBar from '../../Containers/LoLExaminationRoom/LoLExamRoomAppBar';
import { useGetLoLExamByExamPaperIdQuery as useGetLoLExamByExamPaperId } from '../../generated/graphql';
import { DEFAULT_MILESTONES } from '../LoLMilestones/constants';

import CareerList from './Containers/CareerList';
import CoreTraitList from './Containers/CoreTraitList';
import { MilestoneTags } from './types';

// TODO: refactor LoLFullReport to be as normal Route element. (add the container to fetch/render lists insted of LoLFullReport)
const LoLFullReport = () => {
  const { examPaperId = '' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('lolResults');

  const { data: examinationDataByExamPaper, loading: examinationDataByExamPaperLoading } =
    useGetLoLExamByExamPaperId({
      variables: { examPaperId },
      fetchPolicy: 'network-only',
      skip: examPaperId === '' || !examPaperId,
    });

  const examData = examinationDataByExamPaper?.qmsFoGetExamPaperById.exam;
  const examPaperItems = examinationDataByExamPaper?.qmsFoGetExamPaperById.examPaperItems ?? [];

  const examItems = examData?.examItems ?? [];
  const sortedExamItems = [...examItems].sort((current, next) => current.order - next.order);
  const examQuestions = sortedExamItems.map((examItem) => ({
    id: examItem.question?.id,
    order: examItem.order,
    tags: examItem.question?.tags,
  }));

  const examQuestionByTags = tidy(
    examQuestions.map((examItem) => {
      return {
        id: examItem.id,
        order: examItem?.order,
        tag: examItem.tags?.find(({ category }) => category === 'SUBJECT')?.name,
      };
    }),
    groupBy('tag', summarize({ questions: (items) => items.map((item) => item) }))
  );

  const examQuestionByMilestones = examQuestionByTags.flatMap(({ tag, questions }) => {
    return {
      tag,
      questions,
      isFinished: examPaperItems
        .filter((examPaperItem) =>
          questions.some((question) => question.id === examPaperItem.questionId)
        )
        .every((question) => Object.keys(question.answer).length > 0),
    };
  });

  const allTags = examQuestionByMilestones.map(({ tag }) => tag);

  const milestones: MilestoneTags[] = useMemo(() => {
    return DEFAULT_MILESTONES.filter((milestone) => {
      return milestone.tags.some((tag) => allTags.includes(tag));
    }).map((milestone) => {
      const defaultMilestoneTags = milestone.tags;
      const filteredTags = defaultMilestoneTags.filter((tag) => allTags.includes(tag));

      return {
        tags: filteredTags,
      };
    });
  }, [allTags]);

  const finishedSections: string[] = examQuestionByMilestones
    .filter(({ isFinished, tag }) => isFinished && tag !== undefined)
    .map(({ tag }) => tag as string);

  const handleBack = () => {
    navigate(-1);
  };

  if (examinationDataByExamPaperLoading) return <Loading open />;

  return (
    <>
      <LoLExamRoomAppBar title={t('TITLE')} onBack={handleBack} />
      <Flex flexDirection={'column'} gap={5} p={3} overflow={'auto'}>
        {/* TODO: refactor this to `LoLReportList` and using different defaultList */}
        <CoreTraitList
          examPaperId={examPaperId}
          milestones={milestones}
          finishedSections={finishedSections}
        />
        <CareerList
          examPaperId={examPaperId}
          milestones={milestones}
          finishedSections={finishedSections}
        />
      </Flex>
    </>
  );
};

export default LoLFullReport;
